import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Image, Button } from 'react-bootstrap';
import ArrowUp from '../assets/arrowUp.svg';
import ArrowUpWhite from '../assets/arrowUpWhite.svg';
import ChevronDownWhite from '../assets/chevronDownWhite.svg';
import FreeBadge from '../assets/freePlanBadge.svg';
import CubeIcon from '../assets/cubeIcon.svg';
import ThirdBestIcon from '../assets/thirdBestIcon.svg';
import ProductHuntIcon from '../assets/productHuntIcon.svg';
import FiveStarIcon from '../assets/fiveStarIcon.svg';
import OMRReviewsIcon from '../assets/OMRReviewsIcon.svg';
import CeroBrandLogo from '../assets/CeroBrandLogo.svg';
import CeroGray from '../assets/ceroGray.svg';
import ComingSoon from '../assets/comingSoon.svg';
import ProfessionalPlanBg from '../assets/professionalPlanBg.webp';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/cero-linkedin-personal-br/moeocagahkkomhnekdhlfjbmomkjoddj?hl=en';

type PlanKey = 'standard' | 'professional';
type FeatureStatus = 'available' | 'unavailable' | 'coming_soon' | 'unlimited' | 'limited';

const MOBILE_PLANS: { key: PlanKey; label: string }[] = [
  { key: 'standard', label: 'Standard' },
  { key: 'professional', label: 'Professional' },
];

const FEATURES: { name: string; mobileName?: string; standard: FeatureStatus; professional: FeatureStatus }[] = [
  { name: 'Post Text Formatting', standard: 'available', professional: 'available' },
  {
    name: 'AI Post Generation - Personal Tone/Style',
    mobileName: 'AI Post Generation',
    standard: 'limited',
    professional: 'unlimited',
  },
  {
    name: 'Carousel & Image Generation - Templates',
    mobileName: 'Carousel & Image Generation',
    standard: 'limited',
    professional: 'unlimited',
  },
  { name: 'In-Platform Editing', standard: 'available', professional: 'available' },
  {
    name: 'AI Engagement - Automated Commenting',
    mobileName: 'AI Engagement',
    standard: 'unavailable',
    professional: 'coming_soon',
  },
  { name: 'LinkedIn Ideas Generator', standard: 'unavailable', professional: 'available' },
  { name: 'Smart Calendar Automation', standard: 'unavailable', professional: 'unavailable' },
  { name: 'Team Collaboration', standard: 'unavailable', professional: 'unavailable' },
];

function renderDesktopFeatureStatus(status: FeatureStatus, isProfessional: boolean) {
  const cellStyle: React.CSSProperties = {
    padding: '16px 24px',
    textAlign: 'center',
    ...(isProfessional ? { background: '#FF800014' } : {}),
  };

  if (status === 'available') {
    return (
      <div style={cellStyle}>
        <Image src={CeroBrandLogo} alt="available" height={18} />
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <div style={cellStyle}>
        <Image src={CeroGray} alt="unavailable" height={18} />
      </div>
    );
  }

  if (status === 'coming_soon') {
    return (
      <div style={cellStyle}>
        <Image src={ComingSoon} alt="coming soon" height={18} />
      </div>
    );
  }

  if (status === 'unlimited') {
    return (
      <div style={{ ...cellStyle, color: '#FFFFFF' }}>
        Unlimited
      </div>
    );
  }

  return (
    <div style={{ ...cellStyle, color: '#FFFFFF6B' }}>
      Limited to 3
    </div>
  );
}

function renderMobileFeatureStatus(status: FeatureStatus) {
  if (status === 'unlimited') {
    return <span className="pricing-mobile-feature-status--unlimited">Unlimited</span>;
  }

  if (status === 'limited') {
    return <span className="pricing-mobile-feature-status--unlimited">Limited to 3</span>;
  }

  if (status === 'available') {
    return <Image src={CeroBrandLogo} alt="available" height={18} />;
  }

  if (status === 'coming_soon') {
    return <Image src={ComingSoon} alt="coming soon" height={18} />;
  }

  return <Image src={CeroGray} alt="unavailable" height={18} />;
}

type PlanPricing = {
  original: number;
  current: number;
};

const PLAN_PRICING: Record<'monthly' | 'yearly', Record<PlanKey, PlanPricing>> = {
  monthly: {
    standard: { original: 34, current: 29 },
    professional: { original: 79, current: 69 },
  },
  yearly: {
    standard: { original: 405, current: 290 },
    professional: { original: 945, current: 690 },
  },
};

function renderPriceDigits(value: number, variant: 'original' | 'current') {
  const digits = String(value).split('');

  return digits.map((digit, index) => (
    <span
      key={`${variant}-${index}`}
      style={{
        color: variant === 'original' ? 'rgba(255,255,255,0.42)' : '#FFFFFF',
        fontFamily: 'Inter, sans-serif',
        fontSize: 56,
        fontWeight: 600,
        textDecoration: variant === 'original' ? 'line-through' : undefined,
        letterSpacing:
          variant === 'original' && index === digits.length - 1 && digits.length === 2
            ? '8.96px'
            : undefined,
      }}
    >
      {digit}
    </span>
  ));
}

function PlanPriceDisplay({
  className,
  pricing,
  period,
}: {
  className: string;
  pricing: PlanPricing;
  period: 'mo' | 'yr';
}) {
  return (
    <div className={className}>
      <span
        style={{
          color: 'rgba(255,255,255,0.42)',
          fontFamily: 'Inter, sans-serif',
          fontSize: 42,
          fontWeight: 600,
          letterSpacing: '5.04px',
        }}
      >
        $
      </span>
      {renderPriceDigits(pricing.original, 'original')}
      {renderPriceDigits(pricing.current, 'current')}
      <span
        style={{
          color: 'rgba(255,255,255,0.42)',
          fontFamily: 'Inter, sans-serif',
          fontSize: 42,
          fontWeight: 500,
        }}
      >
        /{period}
      </span>
    </div>
  );
}

export const PricingSection: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [mobilePlan, setMobilePlan] = useState<PlanKey>('professional');
  const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false);
  const planDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isPlanDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (planDropdownRef.current && !planDropdownRef.current.contains(event.target as Node)) {
        setIsPlanDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPlanDropdownOpen]);

  const selectedMobilePlanLabel =
    MOBILE_PLANS.find((plan) => plan.key === mobilePlan)?.label ?? 'Professional';
  const pricePeriod = billingCycle === 'yearly' ? 'yr' : 'mo';
  const standardPricing = PLAN_PRICING[billingCycle].standard;
  const professionalPricing = PLAN_PRICING[billingCycle].professional;

  return (
    <>
      <style>{`
        .pricing-feature-table {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .pricing-feature-row {
          display: grid;
          grid-template-columns: 40% 30% 30%;
          align-items: center;
          min-width: 420px;
        }

        .pricing-mobile-features {
          display: none;
        }

        .pricing-mobile-plan-dropdown {
          position: relative;
          width: fit-content;
          min-width: 202px;
          margin-left: auto;
          margin-right: auto;
        }

        .pricing-mobile-plan-selector {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          min-height: 56px;
          padding: 20.5px 22px 20.5px 32px;
          border: 1.5px solid rgba(255, 255, 255, 0.72);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: inset 0px 0px 12px 0px rgba(255, 255, 255, 0.12);
          cursor: pointer;
        }

        .pricing-mobile-plan-selector span {
          font-family: Inter, sans-serif;
          font-weight: 500;
          font-size: 20px;
          line-height: normal;
          color: #ffffff;
          white-space: nowrap;
        }

        .pricing-mobile-plan-selector__chevron {
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }

        .pricing-mobile-plan-selector__chevron--open {
          transform: rotate(180deg);
        }

        .pricing-mobile-plan-dropdown__menu {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          z-index: 10;
          width: 100%;
          margin: 0;
          padding: 8px;
          list-style: none;
          border: 1.5px solid rgba(255, 255, 255, 0.72);
          border-radius: 14px;
          background: rgba(20, 20, 20, 0.96);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: inset 0px 0px 12px 0px rgba(255, 255, 255, 0.12);
        }

        .pricing-mobile-plan-dropdown__option {
          width: 100%;
          border: 0;
          border-radius: 8px;
          padding: 12px 16px;
          background: transparent;
          font-family: Inter, sans-serif;
          font-weight: 500;
          font-size: 18px;
          line-height: 24px;
          color: rgba(255, 255, 255, 0.72);
          text-align: left;
          cursor: pointer;
        }

        .pricing-mobile-plan-dropdown__option:hover,
        .pricing-mobile-plan-dropdown__option:focus {
          background: rgba(255, 255, 255, 0.08);
          color: #ffffff;
          outline: none;
        }

        .pricing-mobile-plan-dropdown__option--selected {
          background: rgba(255, 128, 0, 0.12);
          color: #ffffff;
        }

        .pricing-mobile-feature-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .pricing-mobile-feature-row__name {
          font-family: Inter, sans-serif;
          font-weight: 400;
          font-size: 16px;
          line-height: 24px;
          color: #ffffff;
          text-align: left;
        }

        .pricing-mobile-feature-status--unlimited {
          display: inline-flex;
          align-items: center;
          padding: 4px 8px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 6px;
          font-family: Inter, sans-serif;
          font-size: 11px;
          line-height: 16px;
          color: rgba(255, 255, 255, 0.8);
          white-space: nowrap;
        }

        .pricing-mobile-features__cta {
          width: 100%;
          height: 51px;
          margin-top: 24px;
          border-radius: 14px;
          border: 1.5px solid rgba(255, 255, 255, 0.16);
          padding: 16px 18px 17px 28px;
          background: #ff8000;
          box-shadow: inset 0px 0px 6px 0px rgba(255, 255, 255, 0.16);
          color: #030303;
          text-decoration: none;
          font-family: Inter, sans-serif;
          font-weight: 500;
          font-size: 18px;
          line-height: normal;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .pricing-mobile-features__cta:hover,
        .pricing-mobile-features__cta:focus,
        .pricing-mobile-features__cta:active {
          background: #ff8000;
          color: #030303;
          border-color: rgba(255, 255, 255, 0.16);
          box-shadow: inset 0px 0px 6px 0px rgba(255, 255, 255, 0.16);
        }

        @media (max-width: 991.98px) {
          .pricing-feature-table {
            display: none;
          }

          .pricing-mobile-features {
            display: block;
            width: 100%;
            padding-left: 16px;
            padding-right: 16px;
          }
        }

        @media (min-width: 992px) {
          .pricing-mobile-features {
            display: none;
          }
        }

        .pricing-basic-card {
          width: 80%;
          position: relative;
          background: transparent;
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid transparent;
          background-image:
            linear-gradient(transparent, transparent),
            linear-gradient(180deg, rgba(255, 255, 255, 0.0576) 0%, rgba(255, 255, 255, 0.0512) 100%);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          box-shadow: inset 0px 0px 8px 0px #ffffff0a;
          padding: 24px;
        }

        .pricing-basic-card__badge {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pricing-basic-card__plan-name--mobile {
          display: none;
        }

        .pricing-basic-card__price {
          position: absolute;
          top: 29px;
          right: 23px;
          text-align: right;
          line-height: 1;
          white-space: nowrap;
        }

        .pricing-basic-card__headline {
          margin-top: 40px;
          max-width: 859px;
          font-family: Inter, sans-serif;
          font-weight: 500;
          font-size: 52px;
          line-height: 72px;
          letter-spacing: -0.02em;
          color: #ffffff;
        }

        .pricing-basic-card__headline--mobile {
          display: none;
        }

        .pricing-basic-card__cta {
          position: absolute;
          right: 23px;
          bottom: 21px;
          height: 51px;
          border-radius: 14px;
          border: 1.5px solid rgba(255, 255, 255, 0.16);
          padding: 16px 18px 17px 28px;
          background: #ff8000;
          box-shadow: inset 0px 0px 6px 0px rgba(255, 255, 255, 0.16);
          color: #030303;
          text-decoration: none;
        }

        .pricing-basic-card__cta.btn:hover,
        .pricing-basic-card__cta.btn:focus,
        .pricing-basic-card__cta.btn:active {
          background: #ff8000;
          color: #030303;
          border-color: rgba(255, 255, 255, 0.16);
          box-shadow: inset 0px 0px 6px 0px rgba(255, 255, 255, 0.16);
        }

        .pricing-basic-card__cta-label--mobile {
          display: none;
        }

        @media (max-width: 991.98px) {
          .pricing-basic-card-row {
            padding-left: 16px;
            padding-right: 16px;
          }

          .pricing-basic-card {
            width: 100%;
            border-radius: 12px;
            border: 1px solid rgba(255, 255, 255, 0.18);
            background: transparent;
            background-image: none;
            box-shadow: inset 0px 0px 8px 0px rgba(255, 255, 255, 0.04);
            padding: 31px;
            display: flex;
            flex-direction: column;
          }

          .pricing-basic-card::before {
            content: '';
            position: absolute;
            inset: 0;
            background: rgba(255, 255, 255, 0.04);
            border-radius: 12px;
            pointer-events: none;
          }

          .pricing-basic-card > * {
            position: relative;
            z-index: 1;
          }

          .pricing-basic-card__plan-name--desktop {
            display: none;
          }

          .pricing-basic-card__plan-name--mobile {
            display: inline;
          }

          .pricing-basic-card__price {
            position: static;
            align-self: flex-end;
            margin-top: 56px;
          }

          .pricing-basic-card__headline--desktop {
            display: none;
          }

          .pricing-basic-card__headline--mobile {
            display: block;
            margin-top: 24px;
            font-family: Inter, sans-serif;
            font-weight: 500;
            font-size: 24px;
            line-height: normal;
            letter-spacing: -0.56px;
          }

          .pricing-basic-card__cta {
            position: static;
            align-self: flex-start;
            margin-top: 27px;
          }

          .pricing-basic-card__cta-label--desktop {
            display: none;
          }

          .pricing-basic-card__cta-label--mobile {
            display: inline;
          }
        }

        .pricing-professional-card {
          width: 80%;
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.18);
          box-shadow: inset 0px 0px 8px 0px rgba(255, 255, 255, 0.04);
        }

        .pricing-professional-card__bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: 12px;
        }

        .pricing-professional-card__inner {
          position: relative;
          margin: 11px;
          backdrop-filter: blur(50px);
          -webkit-backdrop-filter: blur(50px);
          background: rgba(0, 0, 0, 0.28);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 4px;
          min-height: 277px;
          padding: 21px 23px;
        }

        .pricing-professional-card__headline {
          font-family: Inter, sans-serif;
          font-weight: 500;
          line-height: normal;
          letter-spacing: -0.72px;
          color: #ffffff;
        }

        .pricing-professional-card__headline--desktop {
          margin-top: 20px;
          font-size: 36px;
        }

        .pricing-professional-card__headline--mobile {
          display: none;
        }

        .pricing-professional-card__headline-sub {
          color: rgba(255, 255, 255, 0.42);
        }

        .pricing-professional-card__price {
          position: absolute;
          top: 29px;
          right: 23px;
          text-align: right;
          line-height: 1;
          white-space: nowrap;
        }

        .pricing-professional-card__discount {
          position: absolute;
          top: 98px;
          right: 23px;
          font-family: Inter, sans-serif;
          font-weight: 500;
          font-size: 20px;
          line-height: normal;
          color: rgba(255, 255, 255, 0.88);
          text-align: right;
          white-space: nowrap;
        }

        .pricing-professional-card__cta {
          position: absolute;
          right: 23px;
          bottom: 21px;
          height: 51px;
          border-radius: 14px;
          border: 1.5px solid rgba(255, 255, 255, 0.16);
          padding: 16px 18px 17px 28px;
          background: #ff8000;
          box-shadow: inset 0px 0px 6px 0px rgba(255, 255, 255, 0.16);
          color: #030303;
          text-decoration: none;
        }

        .pricing-professional-card__cta.btn:hover,
        .pricing-professional-card__cta.btn:focus,
        .pricing-professional-card__cta.btn:active {
          background: #ff8000;
          color: #030303;
          border-color: rgba(255, 255, 255, 0.16);
          box-shadow: inset 0px 0px 6px 0px rgba(255, 255, 255, 0.16);
        }

        @media (max-width: 991.98px) {
          .pricing-professional-card-row {
            padding-left: 16px;
            padding-right: 16px;
          }

          .pricing-professional-card {
            width: 100%;
          }

          .pricing-professional-card__inner {
            min-height: 322px;
            display: flex;
            flex-direction: column;
            padding: 19px 21px;
          }

          .pricing-professional-card__headline--desktop {
            display: none;
          }

          .pricing-professional-card__headline--mobile {
            display: block;
            margin-top: 24px;
            font-size: 28px;
            line-height: 36px;
            letter-spacing: -0.56px;
          }

          .pricing-professional-card__price {
            position: static;
            align-self: flex-start;
            margin-top: 16px;
            text-align: left;
          }

          .pricing-professional-card__discount {
            position: static;
            align-self: flex-start;
            margin-top: 6px;
            text-align: left;
          }

          .pricing-professional-card__cta {
            position: static;
            align-self: flex-start;
            margin-top: 17px;
          }
        }
      `}</style>
      <Container
        id="pricing"
        fluid
        className="justify-content-center"
        style={{ paddingTop: 120, paddingBottom: 200 }}
      >
        <Row className="w-100 justify-content-center">
          <Col xs={12} className="d-flex flex-column align-items-center text-center">
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 64,
                lineHeight: '100%',
                letterSpacing: '-2%',
                color: '#FFFFFF',
              }}
            >
              Pricing
            </div>
            <div
              style={{
                marginTop: 20,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 28,
                lineHeight: '42px',
                color: '#FFFFFF6B',
                maxWidth: 1120,
              }}
            >
              Use Cero solo or with your whole team. Upgrade to enable unlimited premium posts,
              content on autopilot, and additional features.
            </div>
          </Col>
        </Row>

        <Row className="w-100 justify-content-center" style={{ marginTop: 40 }}>
          <Col
            xs={12}
            className="d-flex flex-column align-items-center text-center"
            style={{ gap: 8 }}
          >
            <div
              style={{
                width: 300,
                height: 72,
                borderRadius: 99,
                padding: 8,
                border: '1px solid #FFFFFF14',
                boxShadow: 'inset 0px 0px 8px 0px #FFFFFF0A',
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                background: 'transparent',
              }}
            >
              <button
                onClick={() => setBillingCycle('monthly')}
                style={{
                  width: 142,
                  height: 56,
                  borderRadius: 99,
                  border:
                    billingCycle === 'monthly' ? '1.5px solid transparent' : '1px solid transparent',
                  paddingTop: 20,
                  paddingRight: 32,
                  paddingBottom: 20,
                  paddingLeft: 32,
                  background:
                    billingCycle === 'monthly'
                      ? 'linear-gradient(#FF800029, #FF800029), linear-gradient(96.05deg, rgba(255, 128, 0, 0.42) 0%, rgba(255, 128, 0, 0.48) 100%)'
                      : 'transparent',
                  backgroundOrigin: billingCycle === 'monthly' ? 'border-box' : undefined,
                  backgroundClip: billingCycle === 'monthly' ? 'padding-box, border-box' : undefined,
                  boxShadow: billingCycle === 'monthly' ? 'inset 0px 0px 24px 0px #FF80003D' : 'none',
                  backdropFilter: billingCycle === 'monthly' ? 'blur(16px)' : undefined,
                  color: '#FFFFFF',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: 18,
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                Monthly
              </button>

              <button
                onClick={() => setBillingCycle('yearly')}
                style={{
                  width: 142,
                  height: 56,
                  borderRadius: 99,
                  border:
                    billingCycle === 'yearly' ? '1.5px solid transparent' : '1px solid transparent',
                  paddingTop: 20,
                  paddingRight: 32,
                  paddingBottom: 20,
                  paddingLeft: 32,
                  background:
                    billingCycle === 'yearly'
                      ? 'linear-gradient(#FF800029, #FF800029), linear-gradient(96.05deg, rgba(255, 128, 0, 0.42) 0%, rgba(255, 128, 0, 0.48) 100%)'
                      : 'transparent',
                  backgroundOrigin: billingCycle === 'yearly' ? 'border-box' : undefined,
                  backgroundClip: billingCycle === 'yearly' ? 'padding-box, border-box' : undefined,
                  boxShadow: billingCycle === 'yearly' ? 'inset 0px 0px 24px 0px #FF80003D' : 'none',
                  backdropFilter: billingCycle === 'yearly' ? 'blur(16px)' : undefined,
                  color: '#FFFFFF',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: 18,
                  lineHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                Yearly
              </button>
            </div>

            <div
              style={{
                marginTop: 8,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 18,
                lineHeight: '42px',
                color: 'rgba(255,255,255,0.72)',
              }}
            >
              Save 15% by paying yearly
            </div>
          </Col>
        </Row>

        <Row className="w-100 justify-content-center pricing-basic-card-row" style={{ marginTop: 60 }}>
          <Col xs={12} className="d-flex justify-content-center">
            <div className="pricing-basic-card">
              <div className="pricing-basic-card__badge">
                <Image src={FreeBadge} alt="Free badge" height={20} />
                <span
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: 18,
                    lineHeight: '32px',
                    color: '#FFFFFF',
                  }}
                >
                  <span className="pricing-basic-card__plan-name--desktop">Standard</span>
                  <span className="pricing-basic-card__plan-name--mobile">Standard</span>
                </span>
              </div>

              <div className="pricing-basic-card__headline pricing-basic-card__headline--desktop">
                <span style={{ color: '#FFFFFF6B' }}>Great for those who </span>
                <br />
                <span style={{ color: '#FFFFFF6B' }} className="pricing-basic-card__headline-are--desktop">
                  {' '}
                  are{' '}
                </span>
                <span style={{ color: '#FFFFFF' }}>new</span>
                <span style={{ color: '#FFFFFF6B' }}> to </span>
                <span style={{ color: '#FF8000' }}>personal branding</span>
              </div>

              <div className="pricing-basic-card__headline pricing-basic-card__headline--mobile">
                <div>
                  <span style={{ color: '#FFFFFF6B' }}>Great for those who</span>
                </div>
                <div>
                  <span style={{ color: '#FFFFFF6B' }}>are </span>
                  <span style={{ color: '#FFFFFF' }}>new</span>
                  <span style={{ color: '#FFFFFF6B' }}> to </span>
                  <span style={{ color: '#FF8000' }}>personal</span>
                </div>
                <div>
                  <span style={{ color: '#FF8000' }}>branding</span>
                </div>
              </div>

              <PlanPriceDisplay
                className="pricing-basic-card__price"
                pricing={standardPricing}
                period={pricePeriod}
              />

              <Button
                as="a"
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-basic-card__cta d-flex align-items-center justify-content-center gap-2"
                style={{
                  fontWeight: 500,
                  fontSize: 20,
                  lineHeight: 'normal',
                }}
              >
                <span className="pricing-basic-card__cta-label--desktop">Claim Your Free Trial</span>
                <span className="pricing-basic-card__cta-label--mobile">Start for Free</span>
                <Image src={ArrowUp} alt="Go" height={18} />
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="w-100 justify-content-center pricing-professional-card-row" style={{ marginTop: 16 }}>
          <Col xs={12} className="d-flex justify-content-center">
            <div className="pricing-professional-card">
              <div aria-hidden className="pricing-professional-card__bg">
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 12,
                    background: 'rgba(255, 255, 255, 0.04)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'hidden',
                    borderRadius: 12,
                  }}
                >
                  <img
                    src={ProfessionalPlanBg}
                    alt=""
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '275%',
                      maxWidth: 'none',
                      left: 0,
                      top: '-88.91%',
                    }}
                  />
                </div>
              </div>

              <div className="pricing-professional-card__inner">
                <div className="d-flex align-items-center" style={{ gap: 16 }}>
                  <div className="d-flex align-items-center" style={{ gap: 10 }}>
                    <Image src={CubeIcon} alt="" height={20} />
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: 18,
                        lineHeight: '32px',
                        color: '#FFFFFF',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Professional
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 20,
                      padding: '5.5px 8px',
                      borderRadius: 4,
                      background: 'rgba(255, 128, 0, 0.16)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: 10,
                        lineHeight: '32px',
                        color: '#FF8000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Most popular choice
                    </span>
                  </div>
                </div>

                <div className="pricing-professional-card__headline pricing-professional-card__headline--desktop">
                  <div>limited spots monthly.</div>
                  <div className="pricing-professional-card__headline-sub">First come, first serve.</div>
                </div>

                <div className="pricing-professional-card__headline pricing-professional-card__headline--mobile">
                  <div>limited spots monthly.</div>
                  <div className="pricing-professional-card__headline-sub">First come, first serve.</div>
                </div>

                <PlanPriceDisplay
                  className="pricing-professional-card__price"
                  pricing={professionalPricing}
                  period={pricePeriod}
                />

                <div className="pricing-professional-card__discount">15% Earlybird Discount</div>

                <Button
                  as="a"
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pricing-professional-card__cta d-flex align-items-center justify-content-center gap-2"
                  style={{
                    fontWeight: 500,
                    fontSize: 18,
                    lineHeight: 'normal',
                  }}
                >
                  <span>Claim Your Free Trial</span>
                  <Image src={ArrowUp} alt="Go" height={18} />
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="w-100 justify-content-center" style={{ marginTop: 160 }}>
          <Col
            xs={12}
            md={10}
            lg={9}
            xl={8}
            className="d-flex flex-column align-items-center text-center"
            style={{ gap: 20 }}
          >
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 25,
                lineHeight: '100%',
                color: '#FFFFFFF5',
              }}
            >
              Trusted by leading Linkedin Top Voices
            </div>
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 25,
                lineHeight: '100%',
                color: '#FFFFFF6B',
              }}
            >
              From leading creators to established enterprises
            </div>
          </Col>
        </Row>

        <Row className="w-100 justify-content-center" style={{ marginTop: 90 }}>
          <Col xs={12} className="d-flex justify-content-center">
            <div style={{ width: '70%' }}>
              <Row className="g-4 align-items-center justify-content-between">
                <Col
                  xs={12}
                  md={4}
                  className="d-flex flex-column align-items-center text-center"
                  style={{ gap: 8 }}
                >
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: 56,
                      lineHeight: '100%',
                      color: '#FFFFFF',
                      textAlign: 'right',
                    }}
                  >
                    100K+
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: '100%',
                      color: '#FFFFFF',
                    }}
                  >
                    Posts generated
                  </div>
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: '100%',
                      color: '#FFFFFF',
                    }}
                  >
                    270M+ impressions
                  </div>
                </Col>

                <Col
                  xs={12}
                  md={4}
                  className="d-flex flex-column align-items-center text-center"
                  style={{ gap: 12 }}
                >
                  <Image src={ThirdBestIcon} alt="Third best" height={56} />
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: '100%',
                      color: '#FFFFFF',
                    }}
                  >
                    Product of the day
                  </div>
                  <Image src={ProductHuntIcon} alt="Product Hunt" height={36} />
                </Col>

                <Col
                  xs={12}
                  md={4}
                  className="d-flex flex-column align-items-center text-center"
                  style={{ gap: 12 }}
                >
                  <div
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: 56,
                      lineHeight: '100%',
                      color: '#FFFFFF',
                    }}
                  >
                    5.0
                  </div>
                  <Image src={FiveStarIcon} alt="Five stars" height={22} />
                  <Image src={OMRReviewsIcon} alt="OMR Reviews" height={24} />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Compare Features heading */}
        <Row className="w-100 justify-content-center" style={{ marginTop: 120 }}>
          <Col xs={12} className="d-flex flex-column align-items-center text-center">
            <div
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 64,
                lineHeight: '100%',
                letterSpacing: '-2%',
                color: '#FFFFFF',
              }}
            >
              Compare Features
            </div>
            <div
              style={{
                marginTop: 20,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 28,
                lineHeight: '42px',
                color: '#FFFFFF6B',
              }}
            >
              Choose the plan with the features that meet your needs.
            </div>
          </Col>
        </Row>

        <Row className="w-100 justify-content-center" style={{ marginTop: 90 }}>
          <Col xs={12} className="d-flex justify-content-center">
            <div
              className="pricing-feature-table"
              style={{
                width: '80%',
                background: 'transparent',
                borderRadius: 20,
                border: '1px solid transparent',
                boxShadow: 'inset 0px 0px 8px 0px #FFFFFF0A',
              }}
            >
              <div
                className="pricing-feature-row"
                style={{
                  padding: 0,
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  style={{
                    padding: '16px 24px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: 24,
                    lineHeight: '32px',
                    color: '#FFFFFF',
                  }}
                >
                  Core Features
                </div>
                <div
                  style={{
                    padding: '16px 24px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: 24,
                    lineHeight: '32px',
                    color: '#FFFFFF',
                    textAlign: 'center',
                  }}
                >
                  Standard
                </div>
                <div
                  style={{
                    padding: '16px 24px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: 24,
                    lineHeight: '32px',
                    color: '#FFFFFF',
                    textAlign: 'center',
                    background: '#FF800014',
                  }}
                >
                  Professional
                </div>
              </div>

              {/* Post Text Formatting — AAA */}
              {FEATURES.map((feature) => (
                <div
                  key={feature.name}
                  className="pricing-feature-row"
                  style={{
                    padding: 0,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div
                    style={{
                      padding: '16px 24px',
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                    }}
                  >
                    {feature.name}
                  </div>
                  {renderDesktopFeatureStatus(feature.standard, false)}
                  {renderDesktopFeatureStatus(feature.professional, true)}
                </div>
              ))}

              {/* CTA row (no bottom border) */}
              <div
                className="pricing-feature-row"
                style={{
                  padding: 0,
                }}
              >
                {/* Empty first column */}
                <div style={{ padding: '24px 24px' }}></div>

                {/* Basic button - Figma Primary Btn */}
                <div style={{ padding: '24px 24px', textAlign: 'center' }}>
                  <a
                    href={CHROME_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: 173,
                      height: 50,
                      boxSizing: 'border-box',
                      borderRadius: 14,
                      border: '1.5px solid rgba(255, 128, 0, 0.42)',
                      paddingLeft: 28,
                      paddingRight: 18,
                      background: 'rgba(255, 128, 0, 0.04)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      boxShadow: 'inset 0px 0px 16px 0px rgba(255, 128, 0, 0.22)',
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                      Get Started
                    </span>
                    <Image src={ArrowUpWhite} alt="go" height={10} />
                  </a>
                </div>

                {/* Professional button - solid orange like card CTA */}
                <div style={{ padding: '24px 24px', textAlign: 'center', background: '#FF800014' }}>
                  <a
                    href={CHROME_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: 173,
                      height: 50,
                      borderRadius: 14,
                      border: '1px solid transparent',
                      padding: '12px 18px',
                      background: '#FF8000',
                      color: '#000000',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                  >
                    <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                      Get Started
                    </span>
                    <Image src={ArrowUp} alt="go" height={16} />
                  </a>
                </div>
              </div>
            </div>

            <div className="pricing-mobile-features">
              <div ref={planDropdownRef} className="pricing-mobile-plan-dropdown">
                <button
                  type="button"
                  className="pricing-mobile-plan-selector"
                  aria-haspopup="listbox"
                  aria-expanded={isPlanDropdownOpen}
                  aria-label={`Selected plan: ${selectedMobilePlanLabel}. Open plan menu.`}
                  onClick={() => setIsPlanDropdownOpen((open) => !open)}
                >
                  <span>{selectedMobilePlanLabel}</span>
                  <Image
                    src={ChevronDownWhite}
                    alt=""
                    height={20}
                    className={`pricing-mobile-plan-selector__chevron${
                      isPlanDropdownOpen ? ' pricing-mobile-plan-selector__chevron--open' : ''
                    }`}
                  />
                </button>

                {isPlanDropdownOpen && (
                  <ul className="pricing-mobile-plan-dropdown__menu" role="listbox" aria-label="Plans">
                    {MOBILE_PLANS.map((plan) => (
                      <li key={plan.key}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={mobilePlan === plan.key}
                          className={`pricing-mobile-plan-dropdown__option${
                            mobilePlan === plan.key ? ' pricing-mobile-plan-dropdown__option--selected' : ''
                          }`}
                          onClick={() => {
                            setMobilePlan(plan.key);
                            setIsPlanDropdownOpen(false);
                          }}
                        >
                          {plan.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div style={{ marginTop: 24 }}>
                {FEATURES.map((feature) => (
                  <div key={feature.name} className="pricing-mobile-feature-row">
                    <span className="pricing-mobile-feature-row__name">
                      {feature.mobileName ?? feature.name}
                    </span>
                    {renderMobileFeatureStatus(feature[mobilePlan])}
                  </div>
                ))}
              </div>

              <Button
                as="a"
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pricing-mobile-features__cta"
              >
                <span>Claim Your Free Trial</span>
                <Image src={ArrowUp} alt="Go" height={18} />
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};
